// components/prediction/MarketSenseIQ.jsx
// Market Sense IQ: Score + Rank Badge + Check-in Streak + Mood Histogram + Prediction History

import React, { useState, useEffect } from 'react';
import { theme } from '../../theme';

const RANKS = [
  { id: 'bronze',   label: 'BRONZE',   min: 0,   max: 160, badge: '/badge_B.png', color: '#cd7f32' },
  { id: 'silver',   label: 'SILVER',   min: 160, max: 180, badge: '/badge_S.png', color: '#c0c0c0' },
  { id: 'gold',     label: 'GOLD',     min: 180, max: 210, badge: '/badge_G.png', color: '#e3b341' },
  { id: 'platinum', label: 'PLATINUM', min: 210, max: 250, badge: '/badge_P.png', color: '#a8b4c4' },
  { id: 'diamond',  label: 'DIAMOND',  min: 250, max: 300, badge: '/badge_D.png', color: '#b9f2ff' },
];

function getRank(score) {
  for (const rank of RANKS) {
    if (score < rank.max || rank.id === 'diamond') return rank;
  }
  return RANKS[0];
}

function getNextRank(score) {
  const current = getRank(score);
  const idx = RANKS.findIndex(r => r.id === current.id);
  if (idx < RANKS.length - 1) {
    const next = RANKS[idx + 1];
    return { rank: next, pointsNeeded: next.min - score };
  }
  return null;
}

const TIER_COLORS = {
  'big_rise': '#22a06b', 'small_rise': '#57d9a3', 'flat': '#8b949e',
  'small_drop': '#e07a5f', 'big_drop': '#cf4444',
};
const TIER_LABELS = {
  'big_rise': 'Big Rise', 'small_rise': 'Small Rise', 'flat': 'Flat',
  'small_drop': 'Small Drop', 'big_drop': 'Big Drop',
};

// Confetti component (shows on first badge)
function Confetti({ show }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!show) return;
    const colors = ['#e3b341', '#2dd4bf', '#f85149', '#a855f7', '#3b82f6', '#22c55e'];
    const newParticles = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 4 + Math.random() * 6,
      duration: 1.5 + Math.random() * 1,
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => setParticles([]), 3000);
    return () => clearTimeout(timer);
  }, [show]);

  if (particles.length === 0) return null;
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
      <style>{`
        @keyframes confettiFall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(300px) rotate(720deg); opacity: 0; } }
      `}</style>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', left: p.left + '%', top: 0,
          width: p.size + 'px', height: p.size + 'px',
          background: p.color, borderRadius: p.size > 7 ? '50%' : '1px',
          animation: `confettiFall ${p.duration}s ease-out ${p.delay}s forwards`,
        }} />
      ))}
    </div>
  );
}

export function MarketSenseIQ({ iqScore = 150, streak = 0, bestStreak = 0, moodStats, history = [] }) {
  const currentRank = getRank(iqScore);
  const nextRankInfo = getNextRank(iqScore);
  const [timeRange, setTimeRange] = useState(7);
  const [showConfetti, setShowConfetti] = useState(false);

  // Check-in: count how many days have predictions
  const totalCheckIns = history.length;
  const hasCheckedInToday = history.length > 0 && history[0].actualChange === null;

  // Show confetti on first prediction
  useEffect(() => {
    if (totalCheckIns === 1 && hasCheckedInToday) {
      setShowConfetti(true);
    }
  }, [totalCheckIns, hasCheckedInToday]);

  // Filter history by time range
  const filteredHistory = history.slice(0, timeRange);

  // Build mood histogram data from history (last N days)
  const moodHistogram = (() => {
    const moods = { calm: 0, confident: 0, anxious: 0, impulsive: 0 };
    filteredHistory.forEach(row => {
      if (row.mood && moods[row.mood] !== undefined) {
        moods[row.mood]++;
      }
    });
    return moods;
  })();
  const maxMoodCount = Math.max(1, ...Object.values(moodHistogram));

  // Time range toggle button
  const RangeToggle = () => (
    <div style={{ display: 'flex', gap: '4px', background: theme.bg, borderRadius: '8px', padding: '3px' }}>
      {[7, 30].map(d => (
        <button key={d} onClick={() => setTimeRange(d)} style={{
          padding: '5px 14px', borderRadius: '6px', border: 'none', fontSize: '15px', fontWeight: 600,
          background: timeRange === d ? theme.teal : 'transparent',
          color: timeRange === d ? '#0f172a' : theme.textDark,
          cursor: 'pointer', transition: 'all 0.15s ease',
        }}>
          {d}d
        </button>
      ))}
    </div>
  );

  return (
    <div style={{
      background: theme.bgCard,
      border: '1px solid ' + theme.border,
      borderRadius: '14px',
      padding: '32px',
      marginBottom: '28px',
      position: 'relative',
    }}>
      <Confetti show={showConfetti} />

      <h3 style={{ color: theme.text, fontSize: '24px', fontWeight: 700, margin: '0 0 24px' }}>
        Train Your Market Sense IQ
      </h3>

      {/* Top Row: IQ Score + Check-in Streak + Mood Histogram */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>

        {/* === IQ Score Card: Badge LEFT + Text RIGHT === */}
        <div style={{ background: theme.bg, borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* Badge */}
          <div style={{ flexShrink: 0, textAlign: 'center' }}>
            <img src={currentRank.badge} alt={currentRank.label}
              style={{ width: '120px', height: '120px', objectFit: 'contain', marginBottom: '8px' }} />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '3px' }}>
              {RANKS.map(rank => (
                <img key={rank.id} src={rank.badge} alt={rank.label}
                  style={{
                    width: '28px', height: '28px', objectFit: 'contain',
                    opacity: rank.id === currentRank.id ? 1 : 0.2,
                    filter: rank.id === currentRank.id ? 'none' : 'grayscale(100%)',
                  }} />
              ))}
            </div>
          </div>
          {/* Score + Rank text */}
          <div>
            <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px' }}>Your IQ Score</p>
            <p style={{ fontSize: '52px', fontWeight: 700, color: theme.teal, margin: '0 0 2px', lineHeight: 1 }}>{iqScore}</p>
            <p style={{ fontSize: '20px', fontWeight: 700, color: currentRank.color, letterSpacing: '1px', margin: '0 0 6px' }}>
              {currentRank.label}
            </p>
            {nextRankInfo && (
              <p style={{ fontSize: '16px', color: theme.textDark, margin: 0 }}>
                Next: {nextRankInfo.rank.label} at {nextRankInfo.rank.min} (+{nextRankInfo.pointsNeeded} to go)
              </p>
            )}
          </div>
        </div>

        {/* === Check-in & Streak Card — left big number, right details === */}
        <div style={{ background: theme.bg, borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* LEFT: Big check-in number */}
          <div style={{ flexShrink: 0, textAlign: 'center' }}>
            <p style={{ fontSize: '56px', fontWeight: 700, color: hasCheckedInToday ? '#22c55e' : theme.textDark, margin: 0, lineHeight: 1 }}>
              {totalCheckIns}
            </p>
            <p style={{ fontSize: '15px', color: theme.textDark, textTransform: 'uppercase', letterSpacing: '1px', margin: '6px 0 0' }}>
              Check-ins
            </p>
            {hasCheckedInToday && (
              <p style={{ fontSize: '15px', color: '#22c55e', fontWeight: 600, margin: '4px 0 0' }}>
                ✅ Today
              </p>
            )}
          </div>

          {/* RIGHT: Streak + Personal best */}
          <div style={{ flex: 1 }}>
            <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px' }}>
              Accuracy Streak
            </p>
            <p style={{ fontSize: '40px', fontWeight: 700, color: streak > 0 ? '#22c55e' : theme.textDark, margin: '0 0 4px', lineHeight: 1 }}>
              {streak}
            </p>
            <p style={{ fontSize: '17px', color: streak > 0 ? '#22c55e' : theme.textDark, margin: '0 0 12px' }}>
              Consecutive correct days
            </p>
            <div style={{
              display: 'inline-block', background: 'rgba(45, 212, 191, 0.1)',
              border: '1px solid rgba(45, 212, 191, 0.3)', borderRadius: '20px',
              padding: '5px 14px', fontSize: '16px', color: theme.teal,
            }}>
              Personal best: {bestStreak} days
            </div>
          </div>
        </div>

        {/* === Mood Histogram Card — vertical bars, positive up / negative down === */}
        <div style={{ background: theme.bg, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: 0, fontWeight: 600 }}>
              Mood Distribution
            </p>
            <RangeToggle />
          </div>

          {/* Vertical bar chart */}
          {(() => {
            const moodItems = [
              { key: 'calm',      label: 'Calm',      icon: '🧘', color: '#22c55e', positive: true },
              { key: 'confident', label: 'Confident', icon: '💪', color: '#57d9a3', positive: true },
              { key: 'anxious',   label: 'Anxious',   icon: '😰', color: '#e07a5f', positive: false },
              { key: 'impulsive', label: 'Impulsive', icon: '⚡', color: '#cf4444', positive: false },
            ];
            const barMaxHeight = 70; // px max for tallest bar
            return (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '0', position: 'relative' }}>
                {/* Center axis line */}
                <div style={{ position: 'absolute', left: '10%', right: '10%', top: '50%', height: '1px', background: theme.border }} />

                {moodItems.map(item => {
                  const count = moodHistogram[item.key] || 0;
                  const barH = maxMoodCount > 0 ? (count / maxMoodCount) * barMaxHeight : 0;
                  const stats = moodStats?.[item.key];
                  const pct = stats && stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : null;

                  return (
                    <div key={item.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {/* Positive bars go up */}
                      <div style={{ height: barMaxHeight + 'px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                        {item.positive && count > 0 && (
                          <span style={{ fontSize: '16px', fontWeight: 700, color: item.color, marginBottom: '4px' }}>{count}</span>
                        )}
                        {item.positive && (
                          <div style={{
                            width: '36px', height: Math.max(barH, count > 0 ? 4 : 0) + 'px',
                            background: item.color + '50',
                            borderTop: count > 0 ? '3px solid ' + item.color : 'none',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease',
                          }} />
                        )}
                      </div>

                      {/* Center: icon + label */}
                      <div style={{ padding: '6px 0', textAlign: 'center', borderTop: item.positive ? 'none' : undefined, borderBottom: !item.positive ? 'none' : undefined }}>
                        <span style={{ fontSize: '30px', display: 'block' }}>{item.icon}</span>
                        <span style={{ fontSize: '14px', color: theme.textDark, display: 'block', marginTop: '2px', fontWeight: 500 }}>{item.label}</span>
                        {pct != null && (
                          <span style={{ fontSize: '14px', color: item.color, fontWeight: 700, display: 'block' }}>{pct}%</span>
                        )}
                      </div>

                      {/* Negative bars go down */}
                      <div style={{ height: barMaxHeight + 'px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                        {!item.positive && (
                          <div style={{
                            width: '36px', height: Math.max(barH, count > 0 ? 4 : 0) + 'px',
                            background: item.color + '50',
                            borderBottom: count > 0 ? '3px solid ' + item.color : 'none',
                            borderRadius: '0 0 4px 4px',
                            transition: 'height 0.3s ease',
                          }} />
                        )}
                        {!item.positive && count > 0 && (
                          <span style={{ fontSize: '16px', fontWeight: 700, color: item.color, marginTop: '4px' }}>{count}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* === Prediction History Table === */}
      <div style={{ background: theme.bg, borderRadius: '12px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <p style={{ color: theme.text, fontSize: '18px', fontWeight: 700, margin: 0 }}>
            Prediction History
          </p>
          <RangeToggle />
        </div>

        {filteredHistory.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Your Call', 'AI Call', 'Actual', 'You', 'AI', 'Mood', 'IQ'].map(h => (
                  <th key={h} style={{
                    fontSize: '15px', color: theme.textDark, textTransform: 'uppercase',
                    letterSpacing: '0.5px', textAlign: 'left', padding: '10px 12px',
                    borderBottom: '2px solid ' + theme.border, fontWeight: 600,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((row, i) => {
                const isPending = row.actualChange === null;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid ' + theme.bgCard }}>
                    <td style={{ padding: '12px', fontSize: '16px', color: theme.textMuted }}>{row.date}</td>
                    <td style={{ padding: '12px', fontSize: '16px', color: TIER_COLORS[row.userTier] || theme.textMuted, fontWeight: 600 }}>
                      {TIER_LABELS[row.userTier] || row.userTier}
                    </td>
                    <td style={{ padding: '12px', fontSize: '16px', color: TIER_COLORS[row.aiTier] || theme.textMuted, fontWeight: 600 }}>
                      {TIER_LABELS[row.aiTier] || row.aiTier}
                    </td>
                    <td style={{ padding: '12px', fontSize: '16px', color: isPending ? theme.textDark : (row.actualChange >= 0 ? '#22c55e' : '#f85149') }}>
                      {isPending ? (
                        <span style={{ background: 'rgba(45, 212, 191, 0.1)', color: theme.teal, padding: '4px 12px', borderRadius: '6px', fontSize: '15px', fontWeight: 600 }}>Pending</span>
                      ) : (
                        (row.actualChange >= 0 ? '+' : '') + row.actualChange + '%'
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '18px', color: isPending ? theme.textDark : (row.userCorrect ? '#22c55e' : '#f85149') }}>
                      {isPending ? '⏳' : (row.userCorrect ? '✓' : '✗')}
                    </td>
                    <td style={{ padding: '12px', fontSize: '18px', color: isPending ? theme.textDark : (row.aiCorrect ? '#22c55e' : '#f85149') }}>
                      {isPending ? '⏳' : (row.aiCorrect ? '✓' : '✗')}
                    </td>
                    <td style={{ padding: '12px', fontSize: '16px', color: theme.textMuted }}>
                      {row.mood ? row.mood.charAt(0).toUpperCase() + row.mood.slice(1) : '—'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '16px', fontWeight: 700,
                      color: isPending ? theme.textDark : (row.iqChange > 0 ? '#22c55e' : row.iqChange < 0 ? '#f85149' : theme.textDark) }}>
                      {isPending ? '—' : ((row.iqChange > 0 ? '+' : '') + row.iqChange)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '28px 20px' }}>
            <p style={{ color: theme.text, fontSize: '17px', fontWeight: 600, margin: '0 0 10px' }}>
              No records yet.
            </p>
            <p style={{ color: theme.textDark, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
              Open any stock above and pick a direction for tomorrow.<br />
              After market close, your result will appear here: You vs AI vs the actual move.
            </p>
          </div>
        )}
      </div>

      {/* Dynamic Summary based on actual history */}
      <div style={{ marginTop: '20px', padding: '16px 20px', background: 'rgba(45, 212, 191, 0.04)', borderRadius: '10px', borderLeft: '3px solid ' + theme.teal }}>
        <p style={{ color: theme.textMuted, fontSize: '18px', lineHeight: '1.6', margin: 0 }}>
          {(() => {
            const settled = history.filter(h => h.actualChange !== null);
            const correct = settled.filter(h => h.userCorrect);
            const aiCorrect = settled.filter(h => h.aiCorrect);
            const totalPreds = history.length;

            // No history yet
            if (totalPreds === 0) {
              return 'Make your first prediction above to start building your Market Sense IQ! Each correct call earns +1 point, and 3 consecutive correct days earns a bonus +1.';
            }

            // Only pending, no settled yet
            if (settled.length === 0) {
              return `You've made ${totalPreds} prediction${totalPreds > 1 ? 's' : ''} so far — results will be revealed after market close. Keep checking in daily to build your streak!`;
            }

            // Has settled results
            const accuracy = Math.round((correct.length / settled.length) * 100);
            const aiAccuracy = Math.round((aiCorrect.length / settled.length) * 100);
            const beating = accuracy > aiAccuracy;

            // Find dominant mood
            const moodCounts = {};
            settled.forEach(h => { if (h.mood) moodCounts[h.mood] = (moodCounts[h.mood] || 0) + 1; });
            const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0];

            // Find best mood accuracy
            const moodAccMap = {};
            settled.forEach(h => {
              if (h.mood) {
                if (!moodAccMap[h.mood]) moodAccMap[h.mood] = { total: 0, correct: 0 };
                moodAccMap[h.mood].total++;
                if (h.userCorrect) moodAccMap[h.mood].correct++;
              }
            });
            const bestMoodEntry = Object.entries(moodAccMap)
              .filter(([, v]) => v.total >= 2)
              .sort((a, b) => (b[1].correct / b[1].total) - (a[1].correct / a[1].total))[0];

            let summary = `Over ${settled.length} settled prediction${settled.length > 1 ? 's' : ''}, your accuracy is ${accuracy}%${beating ? ' — beating AI\'s ' + aiAccuracy + '%!' : ' vs AI\'s ' + aiAccuracy + '%.'}`;

            if (streak >= 3) {
              summary += ` You're on a ${streak}-day streak — impressive!`;
            } else if (streak > 0) {
              summary += ` Current streak: ${streak} day${streak > 1 ? 's' : ''}.`;
            }

            if (bestMoodEntry) {
              const [moodName, moodData] = bestMoodEntry;
              const moodAcc = Math.round((moodData.correct / moodData.total) * 100);
              summary += ` You tend to be most accurate when feeling ${moodName} (${moodAcc}%).`;
            }

            if (nextRankInfo) {
              summary += ` ${nextRankInfo.pointsNeeded} more point${nextRankInfo.pointsNeeded > 1 ? 's' : ''} to reach ${nextRankInfo.rank.label}.`;
            }

            return summary;
          })()}
        </p>
      </div>
    </div>
  );
}

export default MarketSenseIQ;
